import type { UniqueIdentifier } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type React from 'react';
import { type CSSProperties, type HTMLAttributes, useState } from 'react';

import { Button } from '@/components/Button/Button';
import { useLastHoveredSidebarGroup } from '@/components/Layouts/DragSidebar';
import { useSelectedItems } from '@/components/Menus/SelectMenu';
import SidebarDropdown from '@/components/Sidebars/SidebarDropdown';
import ChatbotIcon from '@/components/ui/ChatbotIcon';
import { Icon } from '@/components/ui/Icon';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useLeftSidebar } from '@/hooks/useLeftSidebar';
import { cn } from '@/utils/utils';
import { ChevronDown, ChevronRight } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import styles from './TreeItem.module.css';
import { getStatusClassnames } from './tree.utils';
import type { FlattenedItem } from './types';

export interface Props extends Omit<HTMLAttributes<HTMLLIElement>, 'id'> {
  childCount?: number;
  clone?: boolean;
  collapsed?: boolean;
  depth: number;
  indentationWidth: number;
  tree: FlattenedItem;
  projectedParentId?: UniqueIdentifier | null;
  onCollapse?(): void;
}

export function SortableTreeItem({
  tree,
  depth,
  indentationWidth,
  clone,
  childCount,
  onCollapse,
  collapsed,
  projectedParentId,
}: Props) {
  const { handleMouseEnter, handleMouseLeave } = useLastHoveredSidebarGroup();
  const {
    attributes,
    isDragging,
    listeners,
    setDraggableNodeRef,
    setDroppableNodeRef,
    transform,
    transition,
    isSorting,
  } = useSortable({
    id: tree.id,
    // animateLayoutChanges,
    data: tree,
  });

  const router = useRouter();

  const style: CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  const { selectedItems, selectItem } = useSelectedItems();

  const width = depth * indentationWidth;

  const isOverFolder = tree.id === projectedParentId && tree.type === 'GROUP';
  const isActive = tree.id === router.query.id;
  const isSelected = selectedItems.some((item) => item.id === tree.id);

  const isMobile = useIsMobile();
  const { setShowLeftSidebar } = useLeftSidebar();

  const [isHovered, setIsHovered] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const showChildIndicator = clone && childCount && childCount > 0;
  return (
    <div
      onMouseEnter={() => {
        setIsHovered(true);
        handleMouseEnter(tree.id as string);
      }}
      onMouseLeave={() => {
        setIsHovered(false);
        handleMouseLeave();
      }}
      className={cn(
        styles.Wrapper,
        'touch-manipulation',
        'select-none', // for preventing text highlighting in the app when pressing shiftKey + click
        'mb-0.5', // Add gap between items
      )}
      ref={tree.id !== 'team' ? setDroppableNodeRef : null}
      {...attributes}
      {...listeners}
      onPointerDown={(e) => {
        if (e.metaKey || e.ctrlKey) return;

        if (e.shiftKey) {
          selectItem(tree);
          return;
        }

        listeners?.onPointerDown?.(e);
      }}
    >
      <div className="flex flex-col" ref={setDraggableNodeRef} style={style}>
        <div
          style={
            {
              paddingLeft: clone ? '0px' : `${width}px`,
            } as React.CSSProperties
          }
          className={cn(
            'relative',
            isActive && !isSorting && 'bg-muted rounded-md',
            'hover:bg-muted rounded-md',
          )}
        >
          <Link
            href={`/b/${tree.id}`}
            prefetch={false}
            onClick={(e) => {
              if (e.shiftKey) {
                e.preventDefault();
                e.stopPropagation();
              }
              if (isMobile) {
                setShowLeftSidebar(false);
              }
            }}
          >
            <Button
              size="xs"
              variant="sidebar"
              className={getStatusClassnames({
                isOverFolder,
                isSelected,
                isDragging,
                clone,
                tree,
              })}
            >
              {!isHovered && tree.type === 'WORKSPACE' && (
                <div className="w-[15px] flex items-center justify-center">
                  <span className="size-[9px] rounded-full bg-muted-foreground" />
                </div>
              )}

              <Icon
                onClick={(e) => {
                  onCollapse?.();
                  e.preventDefault();
                }}
                className="flex-shrink-0 z-10 hover:bg-accent rounded-sm"
                as={
                  isHovered && !sidebarOpen
                    ? collapsed
                      ? ChevronRight
                      : ChevronDown
                    : ChatbotIcon({ type: tree.type, collapsed })
                }
              />

              <span
                className={cn(
                  'text-ellipsis text-primary transition-all ease-in-out duration-200 select-none whitespace-nowrap items-center pr-4 overflow-hidden',
                )}
              >
                {tree.name === '' ? 'Untitled' : tree.name}
              </span>
            </Button>
          </Link>
          <SidebarDropdown
            isActive={isHovered}
            tree={tree}
            onSidebarOpenChange={setSidebarOpen}
          />
        </div>
        {showChildIndicator ? (
          <span className="absolute -top-2 -right-2  bg-blue rounded-[6px] flex items-center justify-center h-4 w-4 z-20 text-xs">
            {childCount}
          </span>
        ) : null}
        {childCount === 0 && !collapsed && !clone && !isDragging && (
          <span
            style={
              {
                paddingLeft: width + 16,
              } as React.CSSProperties
            }
            className="text-muted-foreground text-sm h-[28px] flex items-center  px-4 "
          >
            No subfolders
          </span>
        )}
      </div>
    </div>
  );
}
